import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Col, Container, Row, Table, Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Spinner from 'react-bootstrap/Spinner';
import Tooltip from 'react-bootstrap/Tooltip';
import './ExistingInstances.css';

// Helper function to get display status
const getDisplayStatus = (instance) => {
	if (instance.instance_state === 'stopped') {
		return 'stopped';
	}
	else if (instance.instance_state === 'running') {
		if (instance.instance_status === 'initializing') {
			return 'initializing';
		} else if (instance.instance_status === 'passed') {
			return 'running';
		} else {
			// Status checks did not all pass (e.g. 'failed', 'impaired')
			return 'failed';
		}
	}
	else if (instance.instance_state === 'stopping') {
		return 'stopping';
	}
	else {
		return 'pending';
	}
};

const SITE_TIMEZONES = {
	'Ohio':   'America/Chicago',
	'Oregon': 'America/Los_Angeles',
	'LA':     'America/Los_Angeles',
};

const formatLaunchTime = (launchTime, site) => {
	if (!launchTime) return '-';
	const date = new Date(launchTime);
	if (isNaN(date.getTime())) return '-';
	const tz = SITE_TIMEZONES[site] || 'UTC';
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: tz,
		year: 'numeric', month: '2-digit', day: '2-digit',
		hour: '2-digit', minute: '2-digit',
		hour12: false, timeZoneName: 'short'
	}).formatToParts(date);
	const get = (type) => parts.find(p => p.type === type)?.value || '';
	return `${get('year')}/${get('month')}/${get('day')} ${get('hour')}:${get('minute')} ${get('timeZoneName')}`;
};

export default function ExistingInstances(props) {
	const [allInstances, setAllInstances] = useState([]);
	const [loading, setloading] = useState(true);
	const [error, setError] = useState(null);
	const [showShutdownModal, setShowShutdownModal] = useState(false);
	const [showRebootModal, setShowRebootModal] = useState(false);
	const [instanceToShutdown, setInstanceToShutdown] = useState(null);
	const [instanceToReboot, setInstanceToReboot] = useState(null);
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const [sortConfigs, setSortConfigs] = useState({});
	const isComponentMounted = useRef(true);

	// Handle window resize for mobile detection
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const { apiUrl, userProfile, accessToken, onAuthError, isAdmin, adminMode } = props;

	const fetchData = useCallback(async () => {
		if (!isComponentMounted.current) return;

		setloading(true);
		setError(null);

		const url = apiUrl + "/getInstances";

		// Check if API URL is defined
		if (!apiUrl) {
			const errorMsg = "API URL is not defined. Check REACT_APP_API_URL environment variable.";
			console.error(errorMsg);
			setError(errorMsg);
			setloading(false);
			return;
		}

		try {
			const response = await fetch(url, {
				headers: { "Authorization": `Bearer ${accessToken}` }
			});

			if (!response.ok) {
				if (response.status === 403 && onAuthError) {
					onAuthError();
					return;
				}
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			let json;
			try {
				const text = await response.text();
				json = JSON.parse(text);
			} catch (parseError) {
				const contentType = response.headers.get("content-type");
				throw new Error(`API returned invalid JSON. Content-Type: ${contentType}. Parse error: ${parseError.message}`);
			}

			if (!isComponentMounted.current) return;

			const instances = Array.isArray(json.instances) ? json.instances : [];
			setAllInstances(instances);
			setloading(false);
		} catch (error) {
			console.error("Failed to fetch instances:", error);
			if (isComponentMounted.current) {
				setError(error.message);
				setloading(false);
			}
			return error;
		}
	}, [apiUrl, accessToken, onAuthError]);

	// Simple refresh logic - only on page load and focus
	useEffect(() => {
		isComponentMounted.current = true;
		fetchData();

		// Handle window focus - refresh when coming back to the page
		const handleFocus = () => {
			fetchData();
		};

		window.addEventListener('focus', handleFocus);

		// Cleanup
		return () => {
			isComponentMounted.current = false;
			window.removeEventListener('focus', handleFocus);
		};
	}, [fetchData]);

	// Derive filtered view from raw instances — no re-fetch needed when mode changes
	const data = useMemo(() => {
		if (isAdmin && adminMode) {
			return allInstances;
		} else if (userProfile && userProfile.email) {
			return allInstances.filter(instance => instance.assigned_user === userProfile.email);
		}
		return [];
	}, [allInstances, isAdmin, adminMode, userProfile]);

	const updateInstance = async (requestBody) => {
		const url = props.apiUrl + "/updateInstance";
		try {
			const response = await fetch(url, {
				method: "POST",
				headers: {
					"content-type": "application/json",
					"Authorization": `Bearer ${props.accessToken}`
				},
				body: JSON.stringify(requestBody)
			});
			
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			
			const json = await response.json();
			console.log("Update response:", json); // Debug log
			return json;
		} catch (error) {
			console.error("Failed to update instance:", error);
			throw error;
		}
	};

	const handlePowerOn = async (info) => {
		let requestBody = {
			desiredState: "running",
			signature: "DY",
			instanceId: info.instance_id,
			name: info.name,
			region: info.region
		};
		
		try {
			const json = await updateInstance(requestBody);
			console.log("Power on response:", json);
			
			// Immediate refresh after action
			setTimeout(() => {
				fetchData();
			}, 2000);
			
		} catch (error) {
			console.error("Error powering on instance:", error);
		}
	};

	const handlePowerOff = async (info) => {
		let requestBody = {
			desiredState: "stopped",
			signature: "DY",
			instanceId: info.instance_id,
			name: info.name,
			region: info.region
		};
		
		try {
			const json = await updateInstance(requestBody);
			console.log("Power off response:", json);
			
			// Immediate refresh after action
			setTimeout(() => {
				fetchData();
			}, 2000);
			
		} catch (error) {
			console.error("Error powering off instance:", error);
		}
	};

	const handleReboot = async (info) => {
		let requestBody = {
			desiredState: "reboot",
			signature: "DY",
			instanceId: info.instance_id,
			name: info.name,
			region: info.region
		};
		
		try {
			const json = await updateInstance(requestBody);
			console.log("Reboot response:", json);
			
			// Immediate refresh after action
			setTimeout(() => {
				fetchData();
			}, 2000);
			
		} catch (error) {
			console.error("Error rebooting instance:", error);
		}
	};

	const handleShutdownClick = (info) => {
		setInstanceToShutdown(info);
		setShowShutdownModal(true);
	};

	const handleRebootClick = (info) => {
		setInstanceToReboot(info);
		setShowRebootModal(true);
	};

	const confirmShutdown = () => {
		if (instanceToShutdown) {
			handlePowerOff(instanceToShutdown);
			
			// Refresh after shutdown similar to power on
			setTimeout(() => {
				fetchData();
			}, 2000);
		}
		setShowShutdownModal(false);
		setInstanceToShutdown(null);
	};

	const cancelShutdown = () => {
		setShowShutdownModal(false);
		setInstanceToShutdown(null);
	};

	const confirmReboot = () => {
		if (instanceToReboot) {
			handleReboot(instanceToReboot);
			
			// Refresh after reboot
			setTimeout(() => {
				fetchData();
			}, 2000);
		}
		setShowRebootModal(false);
		setInstanceToReboot(null);
	};

	const cancelReboot = () => {
		setShowRebootModal(false);
		setInstanceToReboot(null);
	};

	const getSiteSort = (site) => sortConfigs[site] || { column: 'assigned_user', direction: 'asc' };

	const handleSort = (site, column) => {
		setSortConfigs(prev => {
			const current = prev[site] || { column: 'assigned_user', direction: 'asc' };
			return {
				...prev,
				[site]: current.column === column
					? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
					: { column, direction: 'asc' }
			};
		});
	};

	const getSortValue = (instance, column) => {
		switch (column) {
			case 'name': return (instance.name || '').toLowerCase();
			case 'assigned_user': return (instance.assigned_user || '').toLowerCase();
			case 'instance_type': return (instance.instance_type || '').toLowerCase();
			case 'launch_time': return instance.launch_time ? new Date(instance.launch_time).getTime() : 0;
			case 'status': return getDisplayStatus(instance);
			default: return '';
		}
	};

	const sortIndicator = (site, column) => {
		const cfg = getSiteSort(site);
		if (cfg.column !== column) return <span className="sort-indicator sort-inactive">↕</span>;
		return <span className="sort-indicator sort-active">{cfg.direction === 'asc' ? '↑' : '↓'}</span>;
	};

	// Group instances by site (Ohio, Oregon, LA)
	const siteOrder = ['Ohio', 'Oregon', 'LA'];
	let SortedDataBySite = {};

	data.forEach((item) => {
		const site = item.site || 'Unknown';
		if (!SortedDataBySite[site]) {
			SortedDataBySite[site] = [];
		}
		SortedDataBySite[site].push(item);
	});

	// Sort instances within each site independently
	Object.keys(SortedDataBySite).forEach(site => {
		const cfg = getSiteSort(site);
		SortedDataBySite[site].sort((a, b) => {
			const aVal = getSortValue(a, cfg.column);
			const bVal = getSortValue(b, cfg.column);
			const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
			return cfg.direction === 'asc' ? cmp : -cmp;
		});
	});

	return (
		<Container fluid className="existing-instances-container">
			{loading && (
				<div className="loading-overlay">
					<Spinner animation="border" variant="info" />
				</div>
			)}
			
			{error && (
				<div className="error-container" style={{
					backgroundColor: '#dc3545',
					color: 'white',
					padding: '1rem',
					borderRadius: '4px',
					margin: '1rem 0',
					textAlign: 'left'
				}}>
					<h5>Error Loading Instances</h5>
					<p>{error}</p>
					<p><strong>Debug Info:</strong></p>
					<ul>
						<li>API URL: {props.apiUrl || 'undefined'}</li>
						<li>Environment: {process.env.NODE_ENV}</li>
						<li>Available env vars: {Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}</li>
					</ul>
					<button 
						onClick={() => fetchData()} 
						style={{
							backgroundColor: 'white',
							color: '#dc3545',
							border: 'none',
							padding: '0.5rem 1rem',
							borderRadius: '4px',
							cursor: 'pointer'
						}}
					>
						Retry
					</button>
				</div>
			)}
			
			{/* Shutdown Confirmation Modal */}
			<Modal show={showShutdownModal} onHide={cancelShutdown} centered>
				<Modal.Header closeButton style={{backgroundColor: '#1c1c1c', color: 'white', borderBottom: '1px solid #444'}}>
					<Modal.Title style={{fontSize: isMobile ? '1.1rem' : '1.25rem'}}>Confirm Shutdown</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{backgroundColor: '#1c1c1c', color: 'white', fontSize: isMobile ? '0.9rem' : '1rem'}}>
					Shut down the instance, {instanceToShutdown?.name}?
				</Modal.Body>
				<Modal.Footer style={{backgroundColor: '#1c1c1c', borderTop: '1px solid #444'}}>
					<Button variant="secondary" onClick={cancelShutdown} size={isMobile ? "sm" : undefined}>
						No
					</Button>
					<Button variant="danger" onClick={confirmShutdown} size={isMobile ? "sm" : undefined}>
						Yes
					</Button>
				</Modal.Footer>
			</Modal>

			{/* Reboot Confirmation Modal */}
			<Modal show={showRebootModal} onHide={cancelReboot} centered>
				<Modal.Header closeButton style={{backgroundColor: '#1c1c1c', color: 'white', borderBottom: '1px solid #444'}}>
					<Modal.Title style={{fontSize: isMobile ? '1.1rem' : '1.25rem'}}>Confirm Reboot</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{backgroundColor: '#1c1c1c', color: 'white', fontSize: isMobile ? '0.9rem' : '1rem'}}>
					Reboot the instance, {instanceToReboot?.name}?
				</Modal.Body>
				<Modal.Footer style={{backgroundColor: '#1c1c1c', borderTop: '1px solid #444'}}>
					<Button variant="secondary" onClick={cancelReboot} size={isMobile ? "sm" : undefined}>
						No
					</Button>
					<Button variant="warning" onClick={confirmReboot} size={isMobile ? "sm" : undefined}>
						Yes
					</Button>
				</Modal.Footer>
			</Modal>
			
			<Row>
				<Col>
					{siteOrder.map((site) => {
						if (!SortedDataBySite[site] || SortedDataBySite[site].length === 0) {
							return null;
						}
						
						return (
							<div key={`site-${site}`} className="site-section">
								<Table className="site-wrapper-table">
									<thead>
										<tr>
											<th className="site-header">
												{
													site.toUpperCase() === "LA" 
														? "LOS ANGELES"
														: site.toUpperCase()
												}
											</th>
										</tr>
									</thead>
										<tr>
											<td className="site-table-wrapper">
												<Table variant="dark" striped bordered hover className="instances-table">
													<thead>
														{(() => {
															const multiRow = SortedDataBySite[site].length > 1;
															const w = adminMode
																? { instance: '14%', user: '30%', type: '9%', launch: '20%', status: '10%', control: '17%' }
																: { instance: '20%', user: '30%', type: '15%', status: '15%', control: '20%' };
															return (
																<tr>
																	<th style={{width: w.instance}} className={`column-header${multiRow ? ' sortable-header' : ''}`} onClick={multiRow ? () => handleSort(site, 'name') : undefined}>Instance {multiRow && sortIndicator(site, 'name')}</th>
																	{(!isMobile || adminMode) && <th style={{width: w.user}} className={`column-header${multiRow ? ' sortable-header' : ''}`} onClick={multiRow ? () => handleSort(site, 'assigned_user') : undefined}>Assigned User {multiRow && sortIndicator(site, 'assigned_user')}</th>}
																	<th style={{width: w.type}} className={`column-header${multiRow ? ' sortable-header' : ''}`} onClick={multiRow ? () => handleSort(site, 'instance_type') : undefined}>Type {multiRow && sortIndicator(site, 'instance_type')}</th>
																	{adminMode && <th style={{width: w.launch}} className={`column-header${multiRow ? ' sortable-header' : ''}`} onClick={multiRow ? () => handleSort(site, 'launch_time') : undefined}>Launched {multiRow && sortIndicator(site, 'launch_time')}</th>}
																	<th style={{width: w.status}} className={`column-header${multiRow ? ' sortable-header' : ''}`} onClick={multiRow ? () => handleSort(site, 'status') : undefined}>Status {multiRow && sortIndicator(site, 'status')}</th>
																	<th style={{width: w.control}} className="column-header">Control</th>
																</tr>
															);
														})()}
													</thead>
													<tbody>
														{SortedDataBySite[site].map((info, i) => {
															const isRunning = info.instance_state === "running" && info.instance_status === "passed";
															const isStopped = info.instance_state === "stopped";
															const isFailed = info.instance_state === "running" && info.instance_status !== "initializing" && info.instance_status !== "passed";
															
															let rowKey = `row${info.instance_id}`;

															return (
																<tr key={rowKey}>
																	<td className="cell-middle" title={info.name}>
																		{info.name}
																	</td>
																	{(!isMobile || adminMode) && (
																		<td className="cell-middle" title={info.assigned_user}>
																			{info.assigned_user || '-'}
																		</td>
																	)}
																	<td className="cell-middle">
																		{info.instance_type ? (
																			<a 
																				href={`https://instances.vantage.sh/aws/ec2/${info.instance_type}?currency=USD&region=${info.region}`}
																				className="instance-type-link"
																				target="_blank"
																				rel="noopener noreferrer"
																				title={`View pricing for ${info.instance_type}`}
																			>
																				{info.instance_type}
																			</a>
																		) : (
																			<span className="text-muted">-</span>
																		)}
																	</td>
																	{adminMode && (
																		<td className="cell-middle" style={{whiteSpace: 'nowrap', fontSize: '0.8em'}}>
																			{formatLaunchTime(info.launch_time, info.site)}
																		</td>
																	)}
																	<td className="cell-middle">
																		<span className={`status-${getDisplayStatus(info)}`}>
																			{getDisplayStatus(info)}
																		</span>
																	</td>
																	<td className="cell-middle">
																		<div className="control-buttons-container">
																			{/* Connect Button */}
																			{isRunning ? (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Connect</Tooltip>}
																				>
																					<a
																						href={`dcv://${info.public_ip}?sessionId=${info.name}&width=1920&height=1080`}
																						className="control-button icon-button connect-button btn btn-success"
																						aria-label="Connect to instance"
																					>
																					</a>
																				</OverlayTrigger>
																				) : (
																					<Button 
																						variant="secondary" 
																						className="control-button icon-button connect-button disabled-button"
																						disabled
																						aria-label="Connect unavailable"
																					>
																					</Button>
																			)}
																			
																			{/* Power Button - On/Off toggle */}
																			{isStopped ? (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Power On</Tooltip>}
																				>
																					<Button 
																						variant="success"
																						className="control-button icon-button power-button"
																						onClick={() => handlePowerOn(info)}
																						aria-label="Power on instance"
																					>
																					</Button>
																				</OverlayTrigger>
																			) : (isRunning || isFailed) ? (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Power Off</Tooltip>}
																				>
																					<Button
																						variant="danger"
																						className="control-button icon-button power-button"
																						onClick={() => handleShutdownClick(info)}
																						aria-label="Power off instance"
																					>
																					</Button>
																				</OverlayTrigger>
																			) : (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Pending...</Tooltip>}
																				>
																					<Button 
																						variant="secondary" 
																						className="control-button icon-button power-button disabled-button"
																						disabled
																						aria-label="Power control unavailable"
																					>
																					</Button>
																				</OverlayTrigger>
																			)}
																			
																			{/* Reboot Button - Only available when fully running */}
																			{isRunning ? (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Reboot</Tooltip>}
																				>
																					<Button 
																						variant="warning"
																						className="control-button icon-button reboot-button"
																						onClick={() => handleRebootClick(info)}
																						aria-label="Reboot instance"
																					>
																					</Button>
																				</OverlayTrigger>
																			) : (
																				<OverlayTrigger
																					placement="top"
																					overlay={<Tooltip>Reboot (unavailable)</Tooltip>}
																				>
																					<Button 
																						variant="secondary" 
																						className="control-button icon-button reboot-button disabled-button"
																						disabled
																						aria-label="Reboot unavailable"
																					>
																					</Button>
																				</OverlayTrigger>
																			)}
																		</div>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</Table>
											</td>
										</tr>
								</Table>
							</div>
						);
					})}
				</Col>
			</Row>
		</Container>
	);
}